/**
 * Toast — React (web) implementation.
 *
 * All styling references the shared constants in Toast.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import type { ToastProps } from './Toast';
import {
  toastColorsFor,
  TOAST_TYPOGRAPHY,
  TOAST_BORDER_RADIUS,
  TOAST_HORIZONTAL_PADDING,
  TOAST_VERTICAL_PADDING,
  TOAST_GAP,
  TOAST_ICON_SIZE,
} from './Toast';

export interface WebToastProps extends ToastProps {
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Toast({
  text,
  secondaryText,
  iconName,
  tone = 'light',
  backgroundColor,
  iconColor,
  textColor,
  secondaryTextColor,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebToastProps) {
  const resolvedColors = toastColorsFor(
    backgroundColor,
    iconColor,
    textColor,
    secondaryTextColor,
    tone,
  );

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: TOAST_HORIZONTAL_PADDING,
    paddingRight: TOAST_HORIZONTAL_PADDING,
    paddingTop: TOAST_VERTICAL_PADDING,
    paddingBottom: TOAST_VERTICAL_PADDING,
    borderRadius: TOAST_BORDER_RADIUS,
    backgroundColor: resolvedColors.background,
    gap: TOAST_GAP,
  };

  const baseTextStyle: React.CSSProperties = {
    fontFamily: TOAST_TYPOGRAPHY.fontFamily,
    fontWeight: TOAST_TYPOGRAPHY.fontWeight,
    fontSize: TOAST_TYPOGRAPHY.fontSize,
    lineHeight: `${TOAST_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: TOAST_TYPOGRAPHY.letterSpacing,
  };

  const iconStyle: React.CSSProperties = {
    width: TOAST_ICON_SIZE,
    height: TOAST_ICON_SIZE,
    color: resolvedColors.icon,
    flexShrink: 0,
  };

  return (
    <span style={containerStyle} data-testid={testId} role="status" aria-label={ariaLabel ?? text}>
      <span style={iconStyle} aria-hidden="true">
        {iconName}
      </span>
      {secondaryText ? (
        <span style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <span style={{ ...baseTextStyle, color: resolvedColors.text }}>{text}</span>
          <span style={{ ...baseTextStyle, color: resolvedColors.secondaryText }}>
            {secondaryText}
          </span>
        </span>
      ) : (
        <span style={{ ...baseTextStyle, color: resolvedColors.text }}>{text}</span>
      )}
    </span>
  );
}
